/**
 * The register record's read shapes, as pure data (13-23): its ref, the columns a record read selects, the sibling
 * read's columns and filter, and the list titles. No Graph client, no write path.
 *
 * Two readers use this module: `services/recordService.ts` (the app) and `scripts/record-read-proof.mjs` (the
 * main session's read-only live proof, D-13-23-6). Both therefore send the same `$expand=fields($select=…)`, built
 * from 13-17's field set (`config/fieldset/registerFields.ts`), so the proof checks the URL the app really sends.
 *
 * - Column selection goes **inside** the expand, `fields($select=A,B)`. The shape `$select=id,fields/X` is refused
 *   with a 400 for every field set, and a swallowed 400 reads as an empty list (graphClient `listItemsExpand`).
 * - Person columns are read by `…LookupId` only (D-13-07-7).
 * - `Reference` (the calculated column) is never read (ISS-008, D-13-17-3): the ref is composed from the item id.
 */

import { REGISTERS } from '../config/registers';
import { liveChoice, liveColumn } from '../config/fieldset/live';
import { RECORD_REGISTERS, REGISTER_FIELDS } from '../config/fieldset/registerFields';
import { SIBLING_FIELDS } from '../config/fieldset/siblings';
import type { RecordRegister } from '../types/records';
import { COMPLETENESS_READS } from './reportCompleteness';

// ---------------------------------------------------------------------------
// Refs (ISS-008: `<PREFIX>-<SP Id>`, never the calculated `Reference`)
// ---------------------------------------------------------------------------

/** Why a ref could not be read. */
export class RecordRefError extends Error {
  readonly code: 'unknown-ref';

  constructor(ref: unknown) {
    super(`Not a register reference: ${typeof ref === 'string' ? JSON.stringify(ref) : String(ref)}`);
    this.name = 'RecordRefError';
    this.code = 'unknown-ref';
  }
}

/** The register's prefix (`INC`, `CMP`, `FB`, `RSK`, `CI`), from `config/registers.ts`. */
export function prefixOf(register: RecordRegister): string {
  return REGISTERS[register].prefix;
}

const BY_PREFIX: ReadonlyMap<string, RecordRegister> = new Map(RECORD_REGISTERS.map((r) => [prefixOf(r), r]));

/** A prefix alternation, longest first so no prefix shadows another. */
const PREFIXES = [...BY_PREFIX.keys()].sort((a, b) => b.length - a.length).join('|');

/** A live ref: a prefix, a hyphen and a positive SP item id (leading zeros tolerated on input). */
export const LIVE_REF_PATTERN = new RegExp(`^(${PREFIXES})-0*([1-9]\\d*)$`, 'i');

/**
 * A practice ref (D-13-23-1): a prefix, `-P`, and a practice counter. The `P` means it can never match
 * `LIVE_REF_PATTERN`, so a practised record can never be read or written as a live one.
 */
export const PRACTICE_REF_PATTERN = new RegExp(`^(${PREFIXES})-P([1-9]\\d*)$`, 'i');

export interface ParsedRef {
  register: RecordRegister;
  /** The SP item id (live) or the practice counter (practice). */
  id: number;
  /** The canonical form: upper-case prefix, no leading zeros. */
  ref: string;
}

function parseWith(pattern: RegExp, ref: unknown, practice: boolean): ParsedRef | null {
  if (typeof ref !== 'string') return null;
  const m = pattern.exec(ref.trim());
  if (!m) return null;
  const prefix = m[1].toUpperCase();
  const register = BY_PREFIX.get(prefix);
  const id = Number(m[2]);
  if (!register || !Number.isSafeInteger(id)) return null;
  return { register, id, ref: practice ? `${prefix}-P${id}` : `${prefix}-${id}` };
}

/** A live ref → `{ register, id, ref }`. Anything else, a practice ref included, throws `unknown-ref`. */
export function parseRef(ref: unknown): ParsedRef {
  const parsed = parseWith(LIVE_REF_PATTERN, ref, false);
  if (!parsed) throw new RecordRefError(ref);
  return parsed;
}

/** A practice ref → `{ register, id, ref }`, or throws `unknown-ref`. */
export function parsePracticeRef(ref: unknown): ParsedRef {
  const parsed = parseWith(PRACTICE_REF_PATTERN, ref, true);
  if (!parsed) throw new RecordRefError(ref);
  return parsed;
}

export function isLiveRef(ref: unknown): boolean {
  return parseWith(LIVE_REF_PATTERN, ref, false) !== null;
}

export function isPracticeRef(ref: unknown): boolean {
  return parseWith(PRACTICE_REF_PATTERN, ref, true) !== null;
}

/** `<PREFIX>-<id>` from the SP item id Graph returned (ISS-008). */
export function composeRef(register: RecordRegister, id: number | string): string {
  const n = Number(id);
  if (!Number.isSafeInteger(n) || n < 1) throw new RecordRefError(`${prefixOf(register)}-${String(id)}`);
  return `${prefixOf(register)}-${n}`;
}

/** `<PREFIX>-P<n>`, a practice ref (D-13-23-1). */
export function composePracticeRef(register: RecordRegister, n: number): string {
  if (!Number.isSafeInteger(n) || n < 1) throw new RecordRefError(`${prefixOf(register)}-P${String(n)}`);
  return `${prefixOf(register)}-P${n}`;
}

// ---------------------------------------------------------------------------
// The record read
// ---------------------------------------------------------------------------

/** The built-ins every record read adds (the plan: `Created`, `Modified`). */
export const RECORD_BUILT_INS: readonly string[] = ['Created', 'Modified'];

/**
 * Columns outside the field set that a panel reads as context (13-27): the C3 card's draft on an Incident, which the
 * reportability panel shows collapsed under "The agent's draft, as written" (the D-13-10-1 pattern). No dashboard
 * verb writes them (FIELDSET §1.3: their author is the C3 engine). They are read in a **separate**, small item GET
 * when the decider opens the draft (`recordService.getRecordColumns`), never added to `recordSelect`: the Incidents
 * record read is 2,045 characters, at the 2,048 limit (ISS-261). Each must be a live column, checked at load.
 */
export const RECORD_CONTEXT_READS: Readonly<Partial<Record<RecordRegister, readonly string[]>>> = {
  incidents: ['ReportabilityAssessment', 'Confidence', 'DraftSeverity'],
};
for (const [register, columns] of Object.entries(RECORD_CONTEXT_READS) as [RecordRegister, readonly string[]][]) {
  for (const column of columns) {
    if (!liveColumn(register, column)) throw new Error(`recordQuery: ${register}.${column} is not a live column`);
  }
}

/**
 * Every field-set column of the register, in field-set order, then `Created` and `Modified`; a `person-frozen`
 * column as `<Name>LookupId` (D-13-07-7). No duplicates (`Created` is already a display-only field-set column).
 */
export function recordSelect(register: RecordRegister): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (name: string) => {
    if (!seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  };
  for (const spec of REGISTER_FIELDS[register]) add(spec.kind === 'person-frozen' ? `${spec.name}LookupId` : spec.name);
  for (const name of RECORD_BUILT_INS) add(name);
  return out;
}

/** The field-set column a selected name reads (`ReportedByLookupId` → `ReportedBy`). */
export function columnOfSelect(register: RecordRegister, selected: string): string {
  const person = REGISTER_FIELDS[register].find(
    (spec) => spec.kind === 'person-frozen' && `${spec.name}LookupId` === selected,
  );
  return person ? person.name : selected;
}

/** The `$expand` of a list-items read with these columns: `fields($select=A,B)` (graphClient `listItemsExpand`). */
export function expandOf(select: readonly string[]): string {
  const cols = select.map((s) => s.trim()).filter(Boolean);
  return cols.length ? `fields($select=${cols.join(',')})` : 'fields';
}

/** The record read's `$expand`. */
export function recordExpand(register: RecordRegister): string {
  return expandOf(recordSelect(register));
}

// ---------------------------------------------------------------------------
// Today's list reads (13-29): "Records waiting on you"
// ---------------------------------------------------------------------------

/**
 * The columns `recordsWaitingOn` reads, per register, beyond `Title`, `Status`, `BusinessUnit` and `Created`: one
 * paged list read per register the lens touches (`recordQueue.lensRegisters`), never a per-row read (ISS-207). Each
 * is a live column (`recordQueue.test.ts`), the lens gives the same answer from these columns alone as from the whole
 * record (the same test), and each read is measured under the 2,048-character limit as `record-read-proof --dry`
 * measures the record read (ISS-261). The frozen reporting column is never read (D-12.1-04-C).
 */
export const WAITING_READS: Readonly<Record<RecordRegister, readonly string[]>> = {
  incidents: [
    'ResolveBy', 'RecipientName', 'RecipientClientId', 'ReportabilityDecision', 'NotificationDueAt', 'AwareAt',
    'NdisNoticeClass', 'WorkSafeNotifiable', 'WorkerInjured', 'NdisInitialNoticeAt', 'NdisFurtherNoticeAt',
    'SirsNoticeAt', 'WorkSafeNotifiedAt', 'WorkSafeWrittenRecordAt', 'FinalReportRequired', 'FinalReportDueOn',
    'FinalReportGivenAt', 'ClaimReceivedOn', 'ClaimForwardedOn', 'MedicalCertificateReceivedOn',
    'MedicalCertificateForwardedOn', 'MentalInjuryClaim', 'MentalInjuryNotifiedOn', 'RtwKnowledgeOn',
    'RtwPlanningStartedOn',
    // 13.2-04: the CM's draft and the HR & WHS ask (D-13.2-P-5, -6), and every column the report-completeness test
    // reads (`to-complete`), so a stub is never "incomplete" only because the list read left a column out.
    'ReportabilityDraft', 'ReportabilityDraftNote', 'HrWhsTriageNeeded',
    ...COMPLETENESS_READS,
  ],
  // ISS-258 (13-33): the client columns, so "Client id to add" reaches Today for a complaint too.
  complaints: ['TargetResolutionOn', 'RecipientName', 'RecipientClientId'],
  feedback: ['ReceivedOn'],
  risk: ['RiskCategory', 'HazardType'],
  ci: [],
};

const WAITING_COMMON: readonly string[] = ['Title', 'Status', 'BusinessUnit', 'Created'];

/** Today's list read's columns for a register: the common four, then `WAITING_READS`. */
export function waitingSelect(register: RecordRegister): string[] {
  return [...new Set([...WAITING_COMMON, ...WAITING_READS[register]])];
}

/** Today's list read's `$expand`. */
export function waitingExpand(register: RecordRegister): string {
  return expandOf(waitingSelect(register));
}

/**
 * The preparer read (13-29, D-13-L-19): ONE read of the Incidents Audit Log for every `Status → Evaluating` row, so
 * Oversight's lens can tell "waiting for your approval" from "you prepared it" without a sibling read per incident.
 * `recordService.preparedByUpn` then picks each incident's latest such row, as the record page does.
 */
export const PREPARER_SELECT: readonly string[] = [
  SIBLING_FIELDS.parentRef,
  SIBLING_FIELDS.entryTimestamp,
  SIBLING_FIELDS.entryType,
  SIBLING_FIELDS.fieldChanged,
  SIBLING_FIELDS.newValue,
  SIBLING_FIELDS.actorUPN,
];

const [EVALUATING] = liveChoice('incidents', 'Status', 'Evaluating');

export function preparerExpand(): string {
  return expandOf(PREPARER_SELECT);
}

/** `fields/FieldChanged eq 'Status' and fields/NewValue eq 'Evaluating'` (non-indexed: sent with the Prefer header). */
export function preparerFilter(): string {
  return `fields/${SIBLING_FIELDS.fieldChanged} eq ${odataString('Status')} and fields/${SIBLING_FIELDS.newValue} eq ${odataString(EVALUATING)}`;
}

// ---------------------------------------------------------------------------
// 13.2-10: the possible-duplicate read (Oversight's signal on the record page)
// ---------------------------------------------------------------------------

/**
 * The columns `duplicates.possibleDuplicates` reads, and nothing else: one bounded list read of Incidents, filtered on
 * `ReportedOn` (`duplicateFilter`), made only when `duplicateSignalApplies` (Oversight, an unsettled incident that has
 * or could have a twin). Each must be a live column (`recordService.test.ts`).
 */
export const DUPLICATE_READS: readonly string[] = [
  'Title', 'Status', 'ExternalId', 'RecipientClientId', 'RecipientName', 'OccurredOn', 'ReportedOn', 'InjuredWorkerName',
  'Description', 'DuplicateOfRef',
];
for (const column of DUPLICATE_READS) {
  if (!liveColumn('incidents', column)) throw new Error(`recordQuery: incidents.${column} is not a live column`);
}

/** `fields/ReportedOn ge '<from>' and fields/ReportedOn lt '<to>'` (non-indexed: sent with the Prefer header). */
export function duplicateFilter(from: string, to: string): string {
  return `fields/ReportedOn ge ${odataString(from)} and fields/ReportedOn lt ${odataString(to)}`;
}

// ---------------------------------------------------------------------------
// The sibling read (FIELDSET §2.8)
// ---------------------------------------------------------------------------

/**
 * The §2.8 sibling columns, `LogKind` and `ActorUPN` included; the `Actor` Person column as `ActorLookupId`. The
 * Author is the item's `createdBy`, never a column.
 */
export const SIBLING_SELECT: readonly string[] = [
  SIBLING_FIELDS.title,
  SIBLING_FIELDS.parentRef,
  SIBLING_FIELDS.entryTimestamp,
  SIBLING_FIELDS.entryType,
  SIBLING_FIELDS.logKind,
  SIBLING_FIELDS.fieldChanged,
  SIBLING_FIELDS.oldValue,
  SIBLING_FIELDS.newValue,
  SIBLING_FIELDS.note,
  SIBLING_FIELDS.actorUPN,
  SIBLING_FIELDS.actorLookupId,
];

export function siblingExpand(): string {
  return expandOf(SIBLING_SELECT);
}

/** An OData string literal (a quote doubled), as graphClient `odataLiteral`. */
export function odataString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/** The sibling filter: `fields/ParentRef eq '<ref>'` (the shape the Action Register proved live, 3 Oct 2026). */
export function siblingFilter(ref: string): string {
  return `fields/${SIBLING_FIELDS.parentRef} eq ${odataString(ref)}`;
}

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

/** The parent and audit-sibling list titles, and their `useGraphClient` list-id keys. */
export function listsOf(register: RecordRegister): {
  title: string;
  auditTitle: string;
  idKey: string;
  auditIdKey: string;
} {
  const c = REGISTERS[register];
  return { title: c.listTitle, auditTitle: c.auditListTitle, idKey: c.listIdKey, auditIdKey: c.auditListIdKey };
}

export { RECORD_REGISTERS };
